import { Button, Icon, InputAdornment, Stack, TextField } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { IGenericAutoComplete, IStandardAutocomplete } from "../../_common/interface/autocomplete/iStandardAutocomplete";
import { IDropdownData } from "../../_common/interface/dropdown/iDropdownData.interface";
import { DataTypeAutoComplete } from "../autocomplete/dataType.autocomplete";
import { DataTypeMultipleAutoComplete } from "../autocomplete/dataTypeMultiple2.autocomplete";

type FieldType = "autocomplete" | "text" | "date" | null;

interface ITableSearchInput {
    value: any,
    name: string,
    label: string;
    onChange: (event: any) => void,
    placeholder?: string,
    icon?: string;
    hidden?: boolean;
}

interface IDropdownField extends IGenericAutoComplete {
    type: "autocomplete" | "multiple";
    hidden?: boolean;
    groupBy?: keyof IDropdownData;
}

interface ITextField extends ITableSearchInput {
    type?: "text" | "date";
}

type SearchField = IDropdownField | ITextField;

export function readSearchStorage(bucket: string): Record<string, unknown> {
    try {
        const raw = localStorage.getItem(bucket);
        if (!raw) return {};
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === "object" ? parsed : {};
    } catch {
        return {};
    }
}

function searchValuesEqual(a: unknown, b: unknown): boolean {
    if (Array.isArray(a) && Array.isArray(b)) {
        return a.length === b.length && a.every((value, index) => value === b[index]);
    }
    return a === b;
}

function isEmptySearchValue(value: unknown): boolean {
    if (value === "" || value === null || value === undefined) return true;
    if (Array.isArray(value)) return value.length === 0;
    return false;
}

function getClearValue(current: unknown, input?: SearchField): unknown {
    const isMultiple = (input && "type" in input && input.type === "multiple") || Array.isArray(current);
    return isMultiple ? [] : "";
}

function writeSearchStorageField(bucket: string, name: string, value: unknown) {
    const data = readSearchStorage(bucket);
    if (isEmptySearchValue(value)) {
        delete data[name];
    } else {
        data[name] = value;
    }
    if (Object.keys(data).length === 0) {
        localStorage.removeItem(bucket);
    } else {
        localStorage.setItem(bucket, JSON.stringify(data));
    }
}

export function SearchTableHeader(params: {
    inputs: Array<SearchField>,
    equalWidth?: boolean,
    minItemWidth?: number,
    /** When set, search field values are restored on load and saved on change. */
    localStorageName?: string,
    showClearButton?: boolean,
}) {

    const hydratedRef = useRef(false);

    useEffect(() => {
        hydratedRef.current = false;
    }, [params.localStorageName]);

    useEffect(() => {
        if (!params.localStorageName || hydratedRef.current || !params.inputs?.length) {
            return;
        }

        const stored = readSearchStorage(params.localStorageName);
        const hasStoredValues = params.inputs.some(
            (input) => stored[input.name] !== undefined && stored[input.name] !== null
        );
        if (!hasStoredValues) {
            hydratedRef.current = true;
            return;
        }

        const applyFilter = params.inputs[0]?.onChange;
        if (!applyFilter) {
            hydratedRef.current = true;
            return;
        }

        applyFilter((filter: Record<string, unknown>) => {
            let changed = false;
            const next = { ...filter };
            for (const [key, storedValue] of Object.entries(stored)) {
                if (storedValue !== undefined && !searchValuesEqual(storedValue, next[key])) {
                    next[key] = storedValue;
                    changed = true;
                }
            }
            return changed ? next : filter;
        });

        hydratedRef.current = true;
        // eslint-disable-next-line react-hooks/exhaustive-deps -- hydrate once per bucket; inputs are read from latest render
    }, [params.localStorageName]);

    const persistField = useCallback((name: string, value: unknown) => {
        if (!params.localStorageName) return;
        writeSearchStorageField(params.localStorageName, name, value);
    }, [params.localStorageName]);

    const clearFields = useCallback(() => {
        const applyFilter = params.inputs[0]?.onChange;
        if (!applyFilter) return;

        const rowKeys = params.inputs.map((input) => input.name);
        const storedKeys = params.localStorageName
            ? Object.keys(readSearchStorage(params.localStorageName))
            : [];
        const clearKeys = Array.from(new Set([...rowKeys, ...storedKeys]));

        applyFilter((filter: Record<string, unknown>) => {
            let changed = false;
            const next = { ...filter };
            for (const key of clearKeys) {
                const input = params.inputs.find((field) => field.name === key);
                const clearValue = getClearValue(next[key], input);
                if (!searchValuesEqual(next[key], clearValue)) {
                    next[key] = clearValue;
                    changed = true;
                }
            }
            return changed ? next : filter;
        });

        if (params.localStorageName) {
            localStorage.removeItem(params.localStorageName);
        }
    }, [params.inputs, params.localStorageName]);

    return (
        <Stack
            direction="row"
            spacing={2}
            sx={{
                mb: 2,
                marginTop: "16px",
                ...(params.equalWidth ? {
                    "& > .search-table-input-item": {
                        flex: 1,
                        minWidth: `${params.minItemWidth || 160}px`,
                        display: "flex",
                        "& > *": { width: "100%" },
                    },
                } : {}),
            }}
        >
            {(params.inputs && params.inputs.length) && params.inputs.filter((m) => !m.hidden).map((m, i: number) =>
                <div className="search-table-input-item" key={`${i}_searchInput_${m.name}`}>
                    <SearchTableInput
                        {...m}
                        onPersistValue={params.localStorageName ? persistField : undefined}
                    />
                </div>
            )}
            {params.showClearButton && (
                <Button
                    variant="contained"
                    color="info"
                    startIcon={<Icon>clear</Icon>}
                    sx={{ minWidth: 110 }}
                    onClick={clearFields}
                >
                    Clear
                </Button>
            )}
        </Stack>
    );
}

type SearchTableInputProps = SearchField & {
    onPersistValue?: (name: string, value: unknown) => void;
};

export function SearchTableInput(params: SearchTableInputProps) {

    const [inputValue, setInputValue] = useState(params.value || '');
    const [initaialLoad, setInitaialLoad] = useState(false);

    useEffect(() => {
        const next = params.value ?? '';
        setInputValue((current: any) => (current === next ? current : next));
    }, [params.value]);

    useEffect(() => {
        if (!initaialLoad) {
            setInitaialLoad(true);
            return;
        }

        const timeout = setTimeout(() => {
            params.onChange((__filter: any) => {
                if (__filter[params.name] === inputValue) return __filter;
                return { ...__filter, [params.name]: inputValue };
            });
            params.onPersistValue?.(params.name, inputValue);
        }, 500);

        return () => clearTimeout(timeout);
    }, [inputValue]);

    function isTableDropdownInput(params: SearchTableInputProps): params is IDropdownField & Pick<SearchTableInputProps, "onPersistValue"> {
        return "dataType" in params;
    }

    const persistAutocomplete = (value: unknown) => {
        params.onChange((__filter: any) => {
            if (__filter[params.name] === value) return __filter;
            return { ...__filter, [params.name]: value };
        });
        params.onPersistValue?.(params.name, value);
    };

    return (
        <>
            {!isTableDropdownInput(params) &&
                <TextField
                    fullWidth
                    label={params.label}
                    variant="outlined"
                    value={inputValue}
                    placeholder={params.placeholder || params.label}
                    name="search"
                    type={params.type || "text"}
                    onChange={(event: any) => { setInputValue(event.target.value); }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Icon>{params.icon || "search"}</Icon>
                            </InputAdornment>
                        ),
                    }}
                />
            }

            {(isTableDropdownInput(params)) &&
                <>
                    {(params.type == "multiple") &&
                        <DataTypeMultipleAutoComplete
                            dataType={params.dataType}
                            name={params.name}
                            label={params.label}
                            value={params.value}
                            groupBy={params.groupBy}
                            hasParent={params.hasParent}
                            parentId={params.parentId}
                            disabled={params.disabled}
                            multiple
                            onChange={(e) => {
                                persistAutocomplete(e.target.value);
                            }}
                            hasFormsValidator={false}
                            fullWidth={true}
                        />
                    }

                    {(params.type == "autocomplete") &&
                        <DataTypeAutoComplete {...params} onChange={(e) => {
                            persistAutocomplete(e.target.value);
                        }}
                            hasFormsValidator={false}
                            fullWidth={true}
                        />
                    }

                </>
            }

        </>
    );
}
